import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit8Notes: UnitNotes = {
  unitId: "unit-8",
  unitName: "Unit 8: Fluids",
  examWeight: "10–15%",
  focus: "This unit applies the conservation laws (mass and energy) and Newton's laws of force studied in Units 1–4 to the specific context of fluids.",
  sections: [
    {
      id: "topic-8-1",
      title: "Topic 8.1: Internal Structure and Density",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💧 The Definition of a Fluid</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A fluid is any substance that has no fixed shape and yields easily to external pressure.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Phases</h4>
                <p className="text-sm text-slate-700 mt-1">Includes both <strong>Liquids</strong> and <strong>Gases</strong>.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">Microscopic</h4>
                <p className="text-sm text-slate-700 mt-1">Interatomic forces are weak, allowing particles to slide (liquids) or move independently (gases).</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-900">Macroscopic</h4>
                <p className="text-sm text-slate-700 mt-1">A fluid takes the shape of its container.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Density (<InlineMath math="\rho" />)</h3>
            <p className="text-slate-700 mb-4">Because fluids flow and change shape, we analyze them using density rather than total mass.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-semibold text-slate-800 mb-2">Equation</h4>
              <BlockMath math="\rho = \frac{m}{V}" />
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="SI Units" 
                definition="kg/m³ (Note: 1 g/cm³ = 1000 kg/m³)."
              />
              <KeyTerm 
                term="Standard Density" 
                definition="The density of water is \rho_{water} \approx 1000 \, \text{kg/m}^3."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 The Ideal Fluid Model</h3>
            <p className="text-slate-700 mb-3">To simplify analysis, AP Physics 1 assumes all fluids are <strong>Ideal Fluids</strong>:</p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <p className="text-sm text-slate-700"><strong>1. Incompressible:</strong> The density (<InlineMath math="\rho" />) is constant. Volume does not change under pressure.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <p className="text-sm text-slate-700"><strong>2. Non-viscous:</strong> Zero internal friction between layers. (e.g., water vs. honey).</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <p className="text-sm text-slate-700"><strong>3. Laminar Flow:</strong> Fluid flows in smooth paths (streamlines) that do not cross; no turbulence.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-2",
      title: "Topic 8.2: Pressure",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Defining Pressure</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="P = \frac{F}{A}" />
              <p className="text-sm text-slate-700 mt-2"><strong>Units:</strong> Pascal (Pa), where <InlineMath math="1 \, \text{Pa} = 1 \, \text{N/m}^2" />.</p>
              <p className="text-sm text-slate-700 mt-1"><strong>Scalar:</strong> Pressure acts in all directions and is always perpendicular to surfaces.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 Hydrostatic Pressure (Pressure vs. Depth)</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4 text-sm">
              <h4 className="font-bold mb-2">Derivation:</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Weight of fluid column: <InlineMath math="F_g = mg = (\rho V)g = (\rho A h)g" /></li>
                <li>Pressure of column: <InlineMath math="P = F_g / A = (\rho A h g) / A = \rho g h" /></li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-xl text-white shadow-lg">
              <h4 className="text-blue-200 font-bold mb-2">The Absolute Pressure Equation</h4>
              <BlockMath math="P = P_0 + \rho g h" />
              <div className="grid grid-cols-2 gap-2 text-xs mt-4 text-blue-100">
                <div><InlineMath math="P" />: Absolute Pressure</div>
                <div><InlineMath math="P_0" />: Surface Pressure (<InlineMath math="\approx 1.0 \times 10^5 \, \text{Pa}" />)</div>
                <div><InlineMath math="\rho" />: Fluid Density</div>
                <div><InlineMath math="h" />: Depth below surface</div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Gauge Pressure & Pascal's Principle</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Gauge Pressure</h4>
                <p className="text-sm text-slate-700 mt-1">The difference between absolute and atmospheric pressure.</p>
                <div className="mt-2 text-center bg-white p-2 rounded">
                  <InlineMath math="P_{gauge} = P_{abs} - P_{atm} = \rho g h" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Pascal's Principle</h4>
                <p className="text-sm text-slate-700 mt-1">Pressure applied to an enclosed fluid is transmitted undiminished.</p>
                <div className="mt-2 text-center bg-white p-2 rounded">
                  <InlineMath math="\frac{F_1}{A_1} = \frac{F_2}{A_2}" />
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-3",
      title: "Topic 8.3: Fluids and Newton’s Laws (Buoyancy)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎈 The Buoyant Force (<InlineMath math="F_b" />)</h3>
            <p className="text-slate-700 mb-4">Cause: Pressure increases with depth; the upward force on the bottom of an object is greater than the downward force on the top.</p>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">Archimedes' Principle</h4>
              <p className="text-sm text-slate-700 mb-3">The buoyant force is equal to the <strong>weight of the fluid displaced</strong>.</p>
              <BlockMath math="F_b = \rho_{fluid} V_{displaced} g" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚢 Sinking vs. Floating</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 border-b pb-1 mb-2">Case A: Submerged (Sinking/Rising)</h4>
                <ul className="text-sm text-slate-700 space-y-2">
                  <li><strong>Sinking:</strong> If <InlineMath math="\rho_{obj} > \rho_{fluid}" />, then <InlineMath math="F_g > F_b" />. Accelerates down.</li>
                  <li><strong>Rising:</strong> If <InlineMath math="\rho_{obj} < \rho_{fluid}" />, then <InlineMath math="F_b > F_g" />. Accelerates up.</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900 border-b border-green-200 pb-1 mb-2">Case B: Floating (Equilibrium)</h4>
                <p className="text-sm text-slate-700">At rest on surface: <InlineMath math="\Sigma F = 0 \rightarrow F_b = F_g" />.</p>
                <div className="mt-3 bg-white p-3 rounded border border-green-100">
                  <h5 className="font-bold text-xs text-green-800 uppercase mb-2 text-center">Fraction Submerged Formula</h5>
                  <BlockMath math="\frac{V_{displaced}}{V_{total}} = \frac{\rho_{obj}}{\rho_{fluid}}" />
                  <p className="text-[11px] text-slate-500 mt-2">Example: Ice (<InlineMath math="917 \, \text{kg/m}^3" />) in water (<InlineMath math="1000 \, \text{kg/m}^3" />) is ~92% submerged.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Apparent Weight</h3>
            <KeyTerm 
              term="Apparent Weight" 
              definition="The scale reading when an object is submerged. It is the actual weight minus the buoyant force."
            />
            <div className="mt-2 bg-slate-50 p-3 rounded text-center">
              <InlineMath math="W_{apparent} = F_{scale} = F_g - F_b" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-4",
      title: "Topic 8.4: Fluids and Conservation Laws",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Continuity Equation (Conservation of Mass)</h3>
            <p className="text-slate-700 mb-4">For an incompressible fluid, mass flow in must equal mass flow out.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Volume Flow Rate (Q)</h4>
                <p className="text-xs text-slate-600">Volume passing a point per unit time (<InlineMath math="\text{m}^3/\text{s}" />).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <BlockMath math="A_1 v_1 = A_2 v_2" />
              </div>
            </div>
            <ImportantNote>
              <strong>Conceptual Implication:</strong> If the pipe gets narrower (Area decreases), the fluid must speed up (velocity increases).
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Bernoulli’s Principle (Conservation of Energy)</h3>
            <p className="text-slate-700 mb-4">Bernoulli’s equation is energy per unit volume (<InlineMath math="Work + PE + KE = \text{constant}" />).</p>
            <div className="overflow-x-auto bg-slate-800 p-4 rounded-lg text-white mb-4">
              <BlockMath math="P_1 + \rho g y_1 + \frac{1}{2}\rho v_1^2 = P_2 + \rho g y_2 + \frac{1}{2}\rho v_2^2" />
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900">Key Concept</h4>
              <p className="text-sm text-slate-700 mt-1">As fluid speed increases, internal pressure decreases (at constant height). This explains airplane wing lift.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕳️ Torricelli’s Theorem</h3>
            <p className="text-sm text-slate-700 mb-4 italic">Specific application for a tank with a hole at depth h below the surface.</p>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 p-3 rounded text-sm">
                <strong>Assumptions:</strong> 1) Both ends open to atmosphere (<InlineMath math="P_{top}=P_{hole}=P_{atm}" />). 2) <InlineMath math="v_{top} \approx 0" />.
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 text-center">
                <h4 className="font-bold text-indigo-900 mb-2 text-xs uppercase">Exit Velocity Result</h4>
                <BlockMath math="v = \sqrt{2gh}" />
                <p className="text-xs text-slate-500 mt-2">Same as an object falling from rest from height h.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-5",
      title: "Experimental Skills & Labs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Common Fluid Labs</h3>
            <div className="space-y-4">
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-900">1. Determining Density</h4>
                <p className="text-sm text-slate-700 mt-1">Measure mass via scale and volume via geometry or displacement. The <strong>slope</strong> of a Mass vs. Volume graph is the density.</p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-900">2. Determining Buoyant Force</h4>
                <p className="text-sm text-slate-700 mt-1">Calculate <InlineMath math="F_b = F_g - W_{app}" />. Verify by weighing the overflow water (it should equal <InlineMath math="F_b" />).</p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-blue-900">3. Verifying Torricelli</h4>
                <p className="text-sm text-slate-700 mt-1">Measure horizontal distance (<InlineMath math="x" />) of water projectile. Use kinematics to find exit velocity and compare to <InlineMath math="\sqrt{2gh}" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-red-800 border-b pb-2 mb-4">⚠️ Common Misconceptions</h3>
            <ul className="space-y-3">
              <li className="flex gap-3 text-sm text-slate-700 bg-red-50 p-3 rounded border border-red-100">
                <span className="font-bold text-red-600">Density Confusion:</span> Always use <InlineMath math="\rho_{fluid}" /> for Buoyancy and <InlineMath math="\rho_{obj}" /> for Weight.
              </li>
              <li className="flex gap-3 text-sm text-slate-700 bg-red-50 p-3 rounded border border-red-100">
                <span className="font-bold text-red-600">Pressure Direction:</span> Pressure does not just push "down"; it pushes in <strong>all directions</strong> equally.
              </li>
              <li className="flex gap-3 text-sm text-slate-700 bg-red-50 p-3 rounded border border-red-100">
                <span className="font-bold text-red-600">Atmosphere:</span> Don't forget <InlineMath math="P_{atm}" /> when calculating absolute pressure.
              </li>
              <li className="flex gap-3 text-sm text-slate-700 bg-red-50 p-3 rounded border border-red-100">
                <span className="font-bold text-red-600">Bernoulli Logic:</span> High velocity causes low pressure. High pressure doesn't "push" to high velocity; it's an energy trade-off.
              </li>
            </ul>
          </section>
        </div>
      )
    }
  ]
};

export default physicsUnit8Notes;